import Cookies, { CookieAttributes } from 'js-cookie'

import { getEnvVar } from './env'

const COOKIES = {
  BREAK_SURVEY_DISPLAYED: 'break_survey_displayed',
  CANCEL_SURVEY_DISPLAYED: 'cancel_survey_displayed',
  CHECKOUT_HANDOFF: 'tovala-checkout-handoff',
  JWT_TOKEN: getEnvVar('JWT_COOKIE_NAME'),
  MEAL_CUSTOMER: 'meal_customer',
}

type CookieName = keyof typeof COOKIES

/**
 * Options for cookies shared with the other Tovala apps on the tovala.com
 * domain. js-cookie silently no-ops on removal unless the domain and path match
 * what was set, so reads, writes and removals all have to go through this.
 */
export function getTovalaCookieOptions(): CookieAttributes {
  return {
    domain:
      // develop-preview is an APP_ENV tied to the my.dev.tovala.com domain and production is
      // my.tovala.com. On other envs (like Vercel preview envs), we don't know what the exact
      // domain will be so we don't explicitly set it.
      getEnvVar('APP_ENV') === 'develop-preview' ||
      getEnvVar('APP_ENV') === 'production'
        ? 'tovala.com'
        : undefined,
    path: '/',
  }
}

export function getCookie(cookieName: CookieName) {
  return Cookies.get(COOKIES[cookieName])
}

export function getLocalStorageItem<ItemType>(key: string) {
  try {
    const item = localStorage.getItem(key)
    if (item) {
      return JSON.parse(item) as ItemType
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    // Pass on error. Code should not depend on local storage operations to be successful.
  }
}

export function removeCookie(
  cookieName: CookieName,
  options?: CookieAttributes,
) {
  Cookies.remove(COOKIES[cookieName], options)
}

export function removeLocalStorageItem(localStorageKey: string) {
  try {
    localStorage.removeItem(localStorageKey)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    // Pass on error. Code should not depend on local storage operations to be successful.
  }
}

export function setCookie({
  content,
  cookieName,
  options,
}: {
  content: string
  cookieName: CookieName
  options: CookieAttributes
}) {
  Cookies.set(COOKIES[cookieName], content, options)
}

export function setLocalStorageItem<ItemType>(key: string, item: ItemType) {
  try {
    const itemStr = JSON.stringify(item)

    localStorage.setItem(key, itemStr)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    // Pass on error. Code should not depend on local storage operations to be successful.
  }
}
